import * as Menu from '@radix-ui/react-dropdown-menu'
import { Check } from 'lucide-react'
import { useNavigate } from 'react-router'
import { Avatar } from './Avatar'
import { MenuContent, menuItemClass as itemClass } from './Menu'
import { useI18n } from '@/i18n/i18n'
import { LOCALES, type Locale } from '@/i18n/messages'
import { authClient } from '@/lib/auth-client'
import { type ThemePref, useTheme } from '@/lib/theme'

export function UserMenu({ id, name, email }: { id: string; name: string; email: string }) {
  const { t, locale, setLocale } = useI18n()
  const [theme, setTheme] = useTheme()
  const navigate = useNavigate()

  return (
    <Menu.Root>
      <Menu.Trigger aria-label={t('menu.account')} className="rounded-full outline-offset-2">
        <Avatar actor={{ kind: 'user', id }} size={32} />
      </Menu.Trigger>
      <MenuContent>
        <div className="px-2 pt-1.5 pb-2">
          <div className="truncate text-[13px] font-medium">{name}</div>
          <div className="truncate text-xs text-muted">{email}</div>
        </div>
        <Menu.Separator className="my-1 h-px bg-line" />
        <Menu.Label className="px-2 pt-1 pb-0.5 text-[11px] font-medium text-muted">
          {t('menu.theme')}
        </Menu.Label>
        <Menu.RadioGroup
          value={theme}
          onValueChange={(v) => {
            setTheme(v as ThemePref)
          }}
        >
          {(['system', 'light', 'dark'] as const).map((p) => (
            <Menu.RadioItem key={p} value={p} className={itemClass}>
              <span className="w-4">
                <Menu.ItemIndicator>
                  <Check size={14} />
                </Menu.ItemIndicator>
              </span>
              {t(`menu.theme.${p}`)}
            </Menu.RadioItem>
          ))}
        </Menu.RadioGroup>
        <Menu.Separator className="my-1 h-px bg-line" />
        <Menu.Label className="px-2 pt-1 pb-0.5 text-[11px] font-medium text-muted">
          {t('menu.language')}
        </Menu.Label>
        <Menu.RadioGroup
          value={locale}
          onValueChange={(v) => {
            setLocale(v as Locale)
          }}
        >
          {LOCALES.map((l) => (
            <Menu.RadioItem key={l.id} value={l.id} className={itemClass}>
              <span className="w-4">
                <Menu.ItemIndicator>
                  <Check size={14} />
                </Menu.ItemIndicator>
              </span>
              <span lang={l.id}>{l.label}</span>
            </Menu.RadioItem>
          ))}
        </Menu.RadioGroup>
        <Menu.Separator className="my-1 h-px bg-line" />
        <Menu.Item
          className={itemClass}
          onSelect={() => {
            void navigate('/settings')
          }}
        >
          <span className="w-4" />
          {t('menu.ai')}
        </Menu.Item>
        <Menu.Separator className="my-1 h-px bg-line" />
        <Menu.Item
          className={itemClass}
          onSelect={() => {
            void authClient.signOut().then(() => navigate('/'))
          }}
        >
          <span className="w-4" />
          {t('menu.signOut')}
        </Menu.Item>
      </MenuContent>
    </Menu.Root>
  )
}
